import * as React from 'react';
import { useState } from 'react';

import { Eye, EyeOff, KeyRound } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Button, Input } from '../../ui';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CredentialInputProps extends Omit<React.ComponentPropsWithoutRef<'input'>, 'type'> {
  /** Aria label for the "hide" (eye-off) toggle button. Injected by parent; English default provided. */
  hideLabel?: string;
  /** Optional leading Lucide icon (defaults to KeyRound). */
  icon?: LucideIcon;
  /** Aria label for the "show" (eye) toggle button. Injected by parent; English default provided. */
  showLabel?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Presentational credential input: leading key icon + trailing eye-reveal toggle.
 * i18n-free — labels are injected by the parent (aria-label pattern used by JwtFormField).
 * No validation or data behaviour — purely visual.
 */
export function CredentialInput({
  className,
  hideLabel = 'Hide',
  icon: Icon = KeyRound,
  showLabel = 'Show',
  ...props
}: CredentialInputProps): React.JSX.Element {
  const [revealed, setRevealed] = useState(false);

  function handleToggle(): void {
    setRevealed((prev) => !prev);
  }

  return (
    <div className="relative flex items-center">
      <span className="pointer-events-none absolute start-3 flex shrink-0 items-center text-muted-foreground">
        <Icon className="h-4 w-4" />
      </span>
      <Input {...props} className={cn('ps-9 pe-10', className)} type={revealed ? 'text' : 'password'} />
      <Button
        aria-label={revealed ? hideLabel : showLabel}
        className="absolute end-1 h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
        onClick={handleToggle}
        size="icon"
        type="button"
        variant="ghost"
      >
        {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
    </div>
  );
}
